import { useContext, useEffect, useRef, useState } from 'react'

import { TelemetryContext } from '../context/TelemetryContext'
import { STUDY_EVENTS } from '../constants/telemetryEvents'
import { SURVEY_FEEDBACK_MAX } from './probeService'

import './probe-modal.scss'

const RATINGS = [1, 2, 3, 4, 5]

/**
 * The short survey on a study condition's Thank You overlay, under the thanks and above the
 * probes' answers (the research lead, 2026-10-28): a 1-5 rating of the overall experience
 * (required), optional feedback, and the gift card note. Once submitted it is one line of
 * thanks. Workspace's useStudySurvey stores and sends it; SURVEY_SHOWN is logged once per page
 * when the form appears, SURVEY_SUBMITTED with the rating and the feedback's length only.
 *   answered: the server or this browser has it; refused: the server refused it (try again);
 *   onSubmit(rating, feedback).
 */
export default function StudySurvey({ answered, refused = false, onSubmit }) {
  const { logTelemetryEvent } = useContext(TelemetryContext)
  const [rating, setRating] = useState(null)
  const [feedback, setFeedback] = useState('')
  const loggedRef = useRef(false)

  useEffect(() => {
    if (answered || loggedRef.current) return
    loggedRef.current = true
    logTelemetryEvent(STUDY_EVENTS.SURVEY_SHOWN, {})
  }, [answered, logTelemetryEvent])

  if (answered) return <p className="study-survey-thanks">Thank you for your feedback!</p>

  const handleSubmit = () => {
    if (!rating) return
    logTelemetryEvent(STUDY_EVENTS.SURVEY_SUBMITTED, { rating, feedbackLength: feedback.trim().length })
    onSubmit(rating, feedback)
  }

  return (
    <div className="study-survey">
      <h3 className="study-survey-title" id="study-survey-rating">How was your overall experience?</h3>
      <div className="study-survey-scale" role="radiogroup" aria-labelledby="study-survey-rating">
        <span className="study-survey-end">1 = Poor</span>
        {RATINGS.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={rating === value}
            className={`study-survey-rating${rating === value ? ' selected' : ''}`}
            onClick={() => setRating(value)}
          >
            {value}
          </button>
        ))}
        <span className="study-survey-end">5 = Excellent</span>
      </div>
      <label className="study-survey-label" htmlFor="study-survey-feedback">
        Any feedback or suggestions to help us improve the platform? (optional)
      </label>
      <textarea
        id="study-survey-feedback"
        className="study-survey-feedback"
        rows={3}
        maxLength={SURVEY_FEEDBACK_MAX}
        value={feedback}
        onChange={(event) => setFeedback(event.target.value)}
      />
      <p className="study-survey-note">Leave feedback for a chance to win a $20 Amazon gift card.</p>
      {refused && <p className="study-survey-error">That did not go through. Please submit again.</p>}
      <button type="button" className="survey-submit" onClick={handleSubmit} disabled={!rating}>
        Submit
      </button>
    </div>
  )
}
